// src/utils/hooks/useSurveyModalRefillGate.js
import { useEffect, useMemo, useState } from 'react'
import api from '../../api/api'
import {
  getRefillSurveyCompleted,
  setRefillSurveyCompleted,
} from '../SurveyRefillFlag'
import { endpoints } from '../../api/apiCommons'
import { SURVEY_CONTACTO } from '../surveys'

// Header de auth: solo `token`
function getTokenHeader() {
  return { token: localStorage.getItem('token') || '' }
}

function coerceBool(value) {
  if (value === true || value === 1) return true
  const s = String(value ?? '').toLowerCase()
  return s === 'true' || s === '1'
}

// Acepta Completado en true/1/"true"/"1"
export function coerceCompleted(obj) {
  const dto = obj && obj.data ? obj.data : obj
  return coerceBool(
    dto?.Completado ?? dto?.completado ?? dto?.COMPLETADO ?? dto?.completed,
  )
}

export function readAplica(obj) {
  const dto = obj && obj.data ? obj.data : obj
  const value = dto?.Aplica ?? dto?.aplica ?? dto?.APLICA
  if (value === undefined || value === null || value === '') return undefined
  return coerceBool(value)
}

async function fetchServerEstado(companyId, codigo = SURVEY_CONTACTO) {
  // usar el mismo túnel que el resto: api (axios) + baseURL same-origin
  const path = endpoints?.surveyEstado || 'Survey/Estado'
  const { data } = await api.get(path, {
    params: {
      sEmpresa: companyId,
      CodigoSurvey: codigo,
      _ts: Date.now(), // rompe caché
    },
    headers: {
      ...getTokenHeader(),
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
    },
  })
  return data
}

/**
 * @param {{
 *   companyId: string|number,
 *   serverCompleted?: boolean,
 *   surveyCode?: string,
 *   requireAplica?: boolean,
 *   showOnError?: boolean,
 * }}
 */
export function useSurveyModalRefillGate({
  companyId,
  serverCompleted,
  surveyCode = SURVEY_CONTACTO,
  requireAplica = false,
  showOnError = false,
}) {
  const compId = useMemo(() => companyId, [companyId])

  const [serverDone, setServerDone] = useState(
    typeof serverCompleted === 'boolean' ? serverCompleted : null,
  )
  const [aplica, setAplica] = useState(requireAplica ? null : true)
  const [loading, setLoading] = useState(typeof serverCompleted !== 'boolean')

  useEffect(() => {
    let cancelled = false

    if (compId === undefined || compId === null || String(compId) === '') {
      setServerDone(true) // true ⇒ shouldShow=false
      setLoading(false)
      return
    }

    if (typeof serverCompleted === 'boolean') {
      setServerDone(serverCompleted)
      setLoading(false)
      if (serverCompleted) setRefillSurveyCompleted(compId, true, surveyCode)
      return
    }

    ;(async () => {
      try {
        setLoading(true)
        const estado = await fetchServerEstado(compId, surveyCode)
        if (cancelled) return
        const done = coerceCompleted(estado)
        const applies = readAplica(estado)
        setServerDone(done)
        setAplica(applies === undefined ? !requireAplica : applies)
        if (done) setRefillSurveyCompleted(compId, true, surveyCode)
      } catch {
        if (!cancelled) {
          setServerDone(!showOnError)
          setAplica(showOnError && !requireAplica)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [compId, surveyCode, serverCompleted, requireAplica, showOnError])

  const localCompleted = useMemo(
    () => getRefillSurveyCompleted(compId, surveyCode),
    [compId, surveyCode],
  )

  const shouldShow = useMemo(() => {
    if (loading) return false
    if (aplica !== true) return false
    if (serverDone === true) return false
    if (serverDone === false) return true
    return !localCompleted
  }, [loading, aplica, serverDone, localCompleted])

  const markCompleted = () => setRefillSurveyCompleted(compId, true, surveyCode)

  return { shouldShow, markCompleted }
}
